/**
 * Lab player-exposure-bet modal — bets for one market on the viewed player.
 */

import { Modal } from '@w7/ui'
import { usePlayerMarketBets } from '@/api/hooks/net-exposure/usePlayerMarketBets'
import { money } from '@/lib/format'
import { betListMatchedUnmatchedAmt } from '@/features/net-exposure/betListMatchedAmt'
import type { WorkstationBet } from '@/features/net-exposure/workstation-fixture'
import '@/features/net-exposure/net-exposure.css'

function formatWhen(iso: string | null): string {
  if (!iso) return '-'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '-'
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
}

function toneClass(value: number): string {
  return value < 0 ? 'w7-ne-neg' : 'w7-ne-pos'
}

function BetRows({ bets }: { bets: WorkstationBet[] }) {
  // Agent modal: no data-xscroll — that gate pins last-col sticky and, inside a
  // modal card, paints Profit/Liability over scrolled cells (Market/Selection).
  // Keep --w7-table-sticky-top: 0 for thead; horizontal scroll via CSS only.
  return (
    <div
      className="w7-tablewrap w7-ne-bets-modal"
      style={{ ['--w7-table-sticky-top' as string]: '0px' }}
    >
      <table className="w7-table">
        <thead>
          <tr>
            <th>Member</th>
            <th>Place time</th>
            <th>Matched Time</th>
            <th>Event</th>
            <th>Market</th>
            <th>Selection</th>
            <th>Odds Placed</th>
            <th>Odds Matched</th>
            <th>Matched</th>
            <th>Unmatched</th>
            <th>Profit/Liability</th>
          </tr>
        </thead>
        <tbody>
          {bets.map((bet) => {
            const marketName =
              bet.marketName === 'Multi Bet' ? 'Genie Combo Special' : bet.marketName
            return (
              <tr key={bet.id}>
                <td>{bet.userName || '-'}</td>
                <td>{formatWhen(bet.placedAt)}</td>
                <td>
                  {bet.status === 'EXECUTION_COMPLETE' ? formatWhen(bet.matchedAt) : '-'}
                </td>
                <td>{bet.event || '-'}</td>
                <td>{marketName || '-'}</td>
                <td>
                  <span data-side={bet.type}>{bet.selectionName || '-'}</span>
                </td>
                <td className="w7-mono">{bet.betRate || 0}</td>
                <td className="w7-mono">{bet.averagePriceMatched || 0}</td>
                <td className="w7-mono">
                  {betListMatchedUnmatchedAmt(bet.matchedAmt, bet.currencyType, bet.marketName)}
                </td>
                <td className="w7-mono">
                  {betListMatchedUnmatchedAmt(bet.unMatchedAmt, bet.currencyType, bet.marketName)}
                </td>
                <td className={`w7-mono ${toneClass(bet.profitLiability)}`}>
                  {money(bet.profitLiability, { round: true })}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export function PlayerExposureBetsModal({
  open,
  onClose,
  playerId,
  playerUsername,
  marketId,
  marketName,
}: {
  open: boolean
  onClose: () => void
  playerId: string
  playerUsername: string
  marketId: string
  marketName: string
}) {
  const betsQ = usePlayerMarketBets(
    open ? { playerId, marketId, playerUsername } : null,
    open,
  )
  const bets = betsQ.data ?? []

  return (
    <Modal open={open} onClose={onClose} title={marketName || 'Bet details'} size="lg">
      {betsQ.isLoading ? (
        <p className="w7-ne-empty">Loading bets…</p>
      ) : betsQ.isError ? (
        <p className="w7-ne-empty">
          Could not load bets
          {betsQ.error instanceof Error && betsQ.error.message
            ? `: ${betsQ.error.message}`
            : ''}
        </p>
      ) : bets.length === 0 ? (
        <p className="w7-ne-empty">No bets found</p>
      ) : (
        <BetRows bets={bets} />
      )}
    </Modal>
  )
}
